import { EditIcon, TextButton } from '@maersktankersdigital/web-components'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { IQuestionnaireField } from '~api/vessels/read'
import { NonEditableCommentRenderer } from '~components/non-editable-comment-renderer'
import { COLORS } from '~theme/colors'
import { Textarea } from '../../ops-dashboard-page/components/comment-box/sub-components/textarea'
import type { Field } from './open-section-modal'

interface CommentDisplayProps extends IQuestionnaireField {
  inputData: Field[] | undefined
  onChangeHandler: (value: string) => void
  isDisabled?: boolean
}

export function InputDisplay({
  id,
  answers,
  inputData,
  onChangeHandler,
  type,
  readOnly,
  isDisabled,
}: CommentDisplayProps) {
  const [isEditingComment, setIsEditingComment] = useState<boolean>()
  const [comment, setComment] = useState<string | undefined>()
  const canEdit = !isDisabled && !readOnly

  useEffect(() => {
    const comment =
      inputData?.find((i) => i.id === id)?.value ?? answers?.[0]?.value

    if (comment === undefined) {
      setIsEditingComment(true)
      setComment(undefined)
      return
    }

    setComment(comment.toString())
  }, [inputData, answers])

  useEffect(() => {
    if (isEditingComment) return

    if (answers?.[0]?.value) {
      onChangeHandler(answers[0].value.toString())
    }
  }, [isEditingComment])

  const toggleEditing = () => {
    setIsEditingComment(!isEditingComment)
  }

  return (
    <InputDisplayRenderer
      isEditingComment={!canEdit ? false : !!isEditingComment}
      comment={comment}
      multiline={type !== 'number'}
      userName={answers?.[0]?.user?.name || ''}
      createdAt={answers?.[0]?.updatedAt || ''}
      onChangeHandler={onChangeHandler}
      toggleEditing={toggleEditing}
      disableEdit={!canEdit}
      type={type}
    />
  )
}

interface InputDisplayRendererProps {
  isEditingComment: boolean
  comment: string | undefined
  multiline?: boolean
  userName: string
  createdAt: string
  onChangeHandler: (value: string) => void
  toggleEditing: () => void
  disableEdit?: boolean
  type?: string
}

export function InputDisplayRenderer({
  isEditingComment,
  comment,
  multiline,
  userName,
  createdAt,
  onChangeHandler,
  toggleEditing,
  disableEdit,
  type,
}: InputDisplayRendererProps) {
  return (
    <>
      {isEditingComment ? (
        <Textarea
          type={type}
          value={comment}
          onChange={(e) => onChangeHandler(e.target.value)}
          sx={{ mt: 0 }}
          multiline={multiline}
          disableInitialFocus
        />
      ) : (
        <NonEditableCommentRenderer
          sx={{
            border: `1px solid ${COLORS.secondary.darkBlue}`,
            borderRadius: '4px',
            p: 3,
            pl: 2,
            width: 'initial',
          }}
          xlGap
          comment={comment}
          userName={userName}
          createdAt={createdAt}
        />
      )}
      {comment && !disableEdit && (
        <Box
          component={TextButton}
          onClick={toggleEditing}
          icon={<EditIcon width="16px" height="16px" />}
          sx={{ pl: 0 }}
        >
          {isEditingComment ? 'Cancel' : 'Edit'}
        </Box>
      )}
    </>
  )
}
